import React from 'react';
import styled, { css } from 'styled-components';
import { colors, typography, transitions, borderRadius, componentSizes } from '../../theme/tokens';
import LogoLoader from '../LogoLoader';

/* Busy state: the platform's orb loader centred over the (transparent) label. Gradient variants keep
   their colour while disabled, so the orb uses its on-dark blend there. */
const FILLED_VARIANTS: string[] = ['primary', 'confirm', 'schedule'];
const BusyOrb = styled.span`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: inline-flex;
  pointer-events: none;
`;

export enum ButtonVariant {
  PRIMARY = 'primary',
  SECONDARY = 'secondary',
  OUTLINE = 'outline',
  GHOST = 'ghost',
  DANGER = 'danger',
  CONFIRM = 'confirm',
  SCHEDULE = 'schedule',
  SCHEDULE_OUTLINE = 'schedule-outline',
  CONTROL_ACTIVE = 'control-active',
  CONTROL_INACTIVE = 'control-inactive',
  CONTROL_DANGER = 'control-danger',
  VIEW_MODE_ACTIVE = 'view-mode-active',
  VIEW_MODE_INACTIVE = 'view-mode-inactive'
}

export enum ButtonSize {
  SM = 'sm',
  MD = 'md',
  LG = 'lg'
}

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  isLoading?: boolean;
  disabled?: boolean;
  round?: boolean;
}

const sizeStyles = {
  [ButtonSize.SM]: css`
    padding: ${componentSizes.button.padding.sm.y} ${componentSizes.button.padding.sm.x};
    font-size: ${typography.fontSize.sm};
    border-radius: ${borderRadius.sm};
  `,
  [ButtonSize.MD]: css`
    padding: ${componentSizes.button.padding.md.y} ${componentSizes.button.padding.md.x};
    font-size: ${typography.fontSize.md};
    border-radius: ${borderRadius.md};
  `,
  [ButtonSize.LG]: css`
    padding: ${componentSizes.button.padding.lg.y} ${componentSizes.button.padding.lg.x};
    font-size: ${typography.fontSize.lg};
    border-radius: ${borderRadius.lg};
  `
};

const variantStyles = {
  [ButtonVariant.PRIMARY]: css`
    background: ${colors.brand.gradient};
    color: #ffffff;
    border: none;
    box-shadow: 0 7px 18px rgba(70, 92, 213, 0.19);

    &:hover:not(:disabled) {
      filter: brightness(1.06);
      box-shadow: 0 11px 22px rgba(70, 92, 213, 0.24);
    }
  `,
  [ButtonVariant.SECONDARY]: css`
    background-color: ${colors.secondary.main};
    color: ${colors.background.default};
    border: none;

    &:hover:not(:disabled) {
      background-color: ${colors.secondary.hover};
    }
  `,
  [ButtonVariant.OUTLINE]: css`
    background-color: transparent;
    color: ${colors.primary.main};
    border: 2px solid ${colors.primary.main};

    &:hover:not(:disabled) {
      background-color: ${colors.primary.main};
      color: ${colors.background.default};
    }
  `,
  [ButtonVariant.GHOST]: css`
    background-color: transparent;
    color: ${colors.primary.main};
    border: none;

    &:hover:not(:disabled) {
      background-color: ${colors.primary.transparent};
    }
  `,
  [ButtonVariant.DANGER]: css`
    background-color: ${colors.danger.main};
    color: ${colors.background.default};
    border: none;

    &:hover:not(:disabled) {
      background-color: ${colors.danger.hover};
    }
  `,
  [ButtonVariant.CONFIRM]: css`
    background: linear-gradient(135deg, #12a571, #1f9d6b);
    color: #ffffff;
    border: 1px solid rgba(20, 150, 100, 0.4);
    backdrop-filter: blur(5px);
    box-shadow: 0 2px 6px rgba(20, 150, 100, 0.3);
    font-weight: 600;

    &:hover:not(:disabled) {
      background: linear-gradient(135deg, #109463, #1a8c5e);
      border-color: rgba(30, 180, 120, 0.5);
      box-shadow: 0 3px 10px rgba(20, 160, 100, 0.4);
      color: white;
      transform: translateY(-1px);
    }

    &:active {
      transform: translateY(1px);
      box-shadow: 0 1px 3px rgba(39, 68, 131, 0.075);
    }
  `,
  [ButtonVariant.SCHEDULE]: css`
    background: ${colors.brand.gradient};
    color: #ffffff;
    border: none;
    box-shadow: 0 7px 18px rgba(70, 92, 213, 0.19);
    font-weight: 600;

    &:hover:not(:disabled) {
      filter: brightness(1.06);
      transform: translateY(-1px);
      box-shadow: 0 11px 22px rgba(70, 92, 213, 0.24);
    }

    &:active {
      transform: translateY(1px);
      box-shadow: 0 1px 3px rgba(39, 68, 131, 0.07);
    }
  `,
  [ButtonVariant.SCHEDULE_OUTLINE]: css`
    background-color: rgba(51, 92, 244, 0.15);
    color: #2c64da;
    border: 1px solid rgba(51, 92, 244, 0.3);
    backdrop-filter: blur(5px);
    font-weight: 500;

    &:hover:not(:disabled) {
      background-color: rgba(51, 92, 244, 0.25);
      color: #2c64da;
      border-color: rgba(51, 92, 244, 0.5);
      box-shadow: 0 2px 8px rgba(51, 92, 244, 0.2);
    }

    &:active {
      transform: translateY(1px);
      box-shadow: 0 1px 3px rgba(39, 68, 131, 0.07);
    }
  `,
  [ButtonVariant.CONTROL_ACTIVE]: css`
    width: 56px;
    height: 56px;
    border-radius: 12px;
    border: 2px solid rgba(31, 157, 107, 0.4);
    background: linear-gradient(135deg, rgba(31, 157, 107, 0.15) 0%, rgba(26, 140, 94, 0.25) 100%);
    color: #0f8f63;
    font-size: 20px;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 8px 32px rgba(31, 157, 107, 0.2);

    &:hover:not(:disabled) {
      transform: scale(1.05);
      border-color: rgba(31, 157, 107, 0.6);
      box-shadow: 0 12px 40px rgba(31, 157, 107, 0.35);
      color: #0f8f63;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      transform: none;
    }
  `,
  [ButtonVariant.CONTROL_INACTIVE]: css`
    width: 56px;
    height: 56px;
    border-radius: 12px;
    border: 2px solid #e0e8f5;
    background: linear-gradient(135deg, #e6ecf6 0%, #f5f8fd 100%);
    color: #33445e;
    font-size: 20px;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 8px 32px rgba(39, 68, 131, 0.06);

    &:hover:not(:disabled) {
      transform: scale(1.05);
      border-color: #b8c7e8;
      box-shadow: 0 12px 40px rgba(39, 68, 131, 0.1);
      color: #192947;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      transform: none;
    }
  `,
  [ButtonVariant.CONTROL_DANGER]: css`
    width: 56px;
    height: 56px;
    border-radius: 12px;
    border: 2px solid rgba(229, 72, 77, 0.4);
    background: linear-gradient(135deg, rgba(229, 72, 77, 0.15) 0%, rgba(211, 47, 47, 0.25) 100%);
    color: #d63c3c;
    font-size: 20px;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 8px 32px rgba(229, 72, 77, 0.2);

    &:hover:not(:disabled) {
      transform: scale(1.05);
      border-color: rgba(229, 72, 77, 0.6);
      box-shadow: 0 12px 40px rgba(229, 72, 77, 0.35);
      color: #d63c3c;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      transform: none;
    }
  `,
  [ButtonVariant.VIEW_MODE_ACTIVE]: css`
    width: 36px;
    height: 36px;
    border-radius: 6px;
    border: 1px solid rgba(31, 157, 107, 0.6);
    background: linear-gradient(135deg, rgba(31, 157, 107, 0.2) 0%, rgba(26, 140, 94, 0.3) 100%);
    color: #0f8f63;
    font-size: 14px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover:not(:disabled) {
      transform: scale(1.05);
      border-color: rgba(31, 157, 107, 0.8);
      color: #0f8f63;
    }
  `,
  [ButtonVariant.VIEW_MODE_INACTIVE]: css`
    width: 36px;
    height: 36px;
    border-radius: 6px;
    border: 1px solid #cad5e9;
    background: linear-gradient(135deg, #e6ecf6 0%, #f5f8fd 100%);
    color: #455776;
    font-size: 14px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover:not(:disabled) {
      transform: scale(1.05);
      border-color: #b8c7e8;
      color: #192947;
    }
  `
};

const StyledButton = styled.button<ButtonProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: ${typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${transitions.duration.normal} ${transitions.timing};
  outline: none;
  white-space: nowrap;

  ${({ size = ButtonSize.MD }) => sizeStyles[size]}
  ${({ variant = ButtonVariant.PRIMARY }) => variantStyles[variant]}
  ${({ fullWidth }) => fullWidth && css`
    width: 100%;
  `}
  ${({ round }) => round && css`
    border-radius: 50%;
    width: auto;
    aspect-ratio: 1;
    padding: 8px;
  `}

  &:disabled {
    background-color: ${colors.background.disabled};
    color: ${colors.text.disabled};
    cursor: not-allowed;
    border: none;
    opacity: 0.7;
  }

  &:focus {
    box-shadow: ${colors.primary.transparent} 0 0 0 3px;
  }

  ${({ isLoading }) => isLoading && css`
    position: relative;
    color: transparent;
    pointer-events: none;
  `}
`;

export const Button: React.FC<ButtonProps> = ({
  children,
  variant = ButtonVariant.PRIMARY,
  size = ButtonSize.MD,
  fullWidth = false,
  isLoading = false,
  disabled = false,
  round = false,
  ...props
}) => {
  return (
    <StyledButton
      variant={variant}
      size={size}
      fullWidth={fullWidth}
      isLoading={isLoading}
      disabled={disabled || isLoading}
      round={round}
      {...props}
    >
      {children}
      {isLoading && (
        <BusyOrb>
          <LogoLoader
            compact
            size={parseInt(componentSizes.button.loader.size, 10) || 18}
            onDark={FILLED_VARIANTS.includes(variant)}
            label="Working"
          />
        </BusyOrb>
      )}
    </StyledButton>
  );
};

export default Button; 